<!--
    NAVIGATION COMPONENT THAT IS RENDERED INSIDE OF ANALYSIS.VUE
    ALLOWS USER TO NAVIGATE BETWEEN THE MAIN COMPONENTS OF THE APP.
    COMPONENT PART OF © AREA 19, OCG WEB APP.
    DEVELOPED IN MONTREAL, QC, CANADA.
    JULY 2023.
  -->

<script>
import LoadSaveAnalysisStateDialog from '@src/components/LoadSaveAnalysisStateDialog.vue';
import { useActiveStudyStore } from '@src/store/active-study';

import { useAlert } from '@/src/composables/useAlert';
import { useAnalysisStore } from '@/src/store/analysis';

import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import { useAppState } from '../store/ocg-app-state';

import NavBarButton from './NavBarButton.vue';

export default {
  components: {
    NavBarButton,
    LoadSaveAnalysisStateDialog,
  },
  data() {
    return {
      appState: useAppState(),
      activeStudyStore: useActiveStudyStore(),
      analysisStore: useAnalysisStore(),
      currentStudyStore: useCurrentStudyStore(),
    };
  },
  computed: {
    selectedTool() {
      return this.appState.currentView;
    },
  },
  created() {
    this.alert = useAlert();
  },

  methods: {
    goHome() {
      if (
        this.selectedTool === 'QualityCheck'
        && !this.activeStudyStore.hasSavedState
      ) {
        this.$refs.loadSaveAnalysisStateDialog
          ?.invokeSaveWarning(
            this.selectedTool,
            this.currentStudyStore.getStudyID,
          )
          .then((result) => {
            if (result) {
              this.$router.push('/home');
            }
          });
      }
      else {
        this.$router.push('/home');
      }
    },
    // Method to select a tool.
    selectTool(tool) {
      if (this.selectedTool === tool) {
        return; // If  already selected, do nothing
      }

      if (
        tool === 'GraphicalAnalysis'
        && !this.analysisStore.isStudyComplete()
      ) {
        const message = `Cannot proceed to Analysis Module.
           Quality Check step is not complete yet. Ensure the segmentation masks and the biomarkers are properly accquired before continuing`;
        this.alert.warning(message);
        return;
      }
      const { hasSavedState } = this.activeStudyStore;
      // Switched warning to auto-save when navigating between modules.
      if (!hasSavedState) {
        this.$refs.loadSaveAnalysisStateDialog
          ?.backgroundAutosave(this.currentStudyStore.getStudyID)
          .then(() => {
            this.alert.info(
              'Your analysis state has been autosaved in the background.',
            );
            this.appState.setView(tool);
          });
        // this.$refs.loadSaveAnalysisStateDialog
        //   ?.invokeSaveWarning(
        //     this.selectedTool,
        //     this.currentStudyStore.getStudyID
        //   )
        //   .then((result) => {
        //     if (result) {
        //       this.appState.setView(tool);
        //     }
        //   });
        return;
      }
      this.appState.setView(tool);
    },

    /**
     * Handles keydown events and performs actions based on the pressed key.
     *
     * @param {Event} event - The keydown event object.
     */
    handleKeyDown(event) {
      // Define a mapping of key presses to tool names
      const keyToToolMap = {
        1: 'QualityCheck',
        2: 'GraphicalAnalysis',
        3: 'ViewerComponent',
        4: 'FunctionComponent',
        5: 'FlowComponent',
        6: 'ReportMaker',
        Escape: '/home',
      };

      // Check if the pressed key is in the mapping and select the corresponding tool or route
      const selectedTool = keyToToolMap[event.key];

      if (selectedTool) {
        if (event.key === 'Escape') {
          this.$router.push(selectedTool);
        }
        else {
          this.selectTool(selectedTool);
        }
      }
    },
  },
};
</script>

<template>
  <!-- Tools section -->
  <div
    class="simple-panel flex flex-column justify-center items-center py-4 px-1 gap-4"
  >
    <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -->
    <!-- <a href="/home" style="text-decoration: none"> -->
    <NavBarButton
      img-source="Patient List.svg"
      tag="Patient List"
      @click.prevent="goHome"
    />
    <!-- </a> -->

    <!-- OCG Analysis Group: Quality Check + Graphical Analysis -->
    <div
      class="nav-group"
      :class="{
        'nav-group--active':
          selectedTool === 'QualityCheck' || selectedTool === 'GraphicalAnalysis',
      }"
    >
      <NavBarButton
        :img-source="
          selectedTool === 'QualityCheck'
            ? 'CheckContours_Active.svg'
            : 'CheckContours.svg'
        "
        tag="OCG Prep."
        long-tag="OCG Preparation"
        :active="selectedTool === 'QualityCheck'"
        @click="selectTool('QualityCheck')"
      />

      <!-- Step connector -->
      <div class="nav-group__connector">
        <span class="connector-dot mt-1" :class="{ filled: selectedTool === 'QualityCheck' || selectedTool === 'GraphicalAnalysis' }" />
        <span class="connector-line" />
        <span class="connector-dot" :class="{ filled: selectedTool === 'GraphicalAnalysis' }" />
      </div>

      <NavBarButton
        :img-source="
          selectedTool === 'GraphicalAnalysis'
            ? 'Analysis_Active.svg'
            : 'Analysis.svg'
        "
        tag="OCG Analysis"
        :active="selectedTool === 'GraphicalAnalysis'"
        @click="selectTool('GraphicalAnalysis')"
      />
    </div>

    <NavBarButton
      :img-source="
        selectedTool === 'ViewerComponent'
          ? 'ViewerComponent_Active.svg'
          : 'ViewerComponent.svg'
      "
      tag="Viewers"
      :active="selectedTool === 'ViewerComponent'"
      @click="selectTool('ViewerComponent')"
    />

    <NavBarButton
      :img-source="
        selectedTool === 'FunctionComponent'
          ? 'FunctionComponent_Active.svg'
          : 'FunctionComponent.svg'
      "
      tag="Function"
      :active="selectedTool === 'FunctionComponent'"
      @click="selectTool('FunctionComponent')"
    />

    <NavBarButton
      :img-source="
        selectedTool === 'FlowComponent'
          ? 'FlowComponent_Active.svg'
          : 'FlowComponent.svg'
      "
      tag="Flow"
      :active="selectedTool === 'FlowComponent'"
      @click="selectTool('FlowComponent')"
    />

    <NavBarButton
      :img-source="
        selectedTool === 'ReportMaker' ? 'Report_Active.svg' : 'Report.svg'
      "
      tag="Report"
      :active="selectedTool === 'ReportMaker'"
      @click="selectTool('ReportMaker')"
    />
  </div>

  <LoadSaveAnalysisStateDialog ref="loadSaveAnalysisStateDialog" />
</template>

<style scoped>
.nav-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  position: relative;
  padding: 8px 6px 6px;
  border-radius: 10px;
  border: 1px solid var(--md-outline-variant);
  background: var(--md-surface-container-low);
  transition:
    border-color 0.25s ease,
    background 0.25s ease;
}

.nav-group--active {
  border-color: var(--md-primary-container);
}

/* Vertical connector between the two steps */
.nav-group__connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  margin: -2px 0;
}

.connector-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  border: 1px solid var(--md-outline-variant);
  background: transparent;
  transition:
    background 0.25s ease,
    border-color 0.25s ease;
}

.connector-dot.filled {
  background: var(--md-primary);
  border-color: var(--md-primary);
}

.connector-line {
  width: 1px;
  height: 6px;
  background: var(--md-outline-variant);
}

.nav-group--active .connector-line {
  background: var(--md-primary-container);
}
</style>
